
import Button from 'components/base/Button';
import { Card, Col, Form, Row, Toast, ToastContainer } from 'react-bootstrap';
import { useMainLayoutContext } from 'providers/MainLayoutProvider';
import { useEffect, useState } from 'react';

import ProfileNavigation from './components/ProfileNavigation';
import { useTranslation } from 'react-i18next';
import ProfileCard from './components/ProfileCard';
//import profileImage from 'assets/img/team/20.webp';
import avatar from 'assets/img/team/150x150/avatar.webp';
import { InterestsState, PreferencesState, ProfileData } from './components/DataDefinitions';
import apiClient from 'helpers/apiService';
import { useAuth } from 'providers/AuthProvider'
import PersonalInfo from './components/PersonalInfo';
import CompanyInfo from './components/CompanyInfo';
import Preferences from './components/Preferences';
import SideBar from './components/SideBar';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';


const UserProfile = () => {
    const { setFooterClass } = useMainLayoutContext();
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();
    const auth = useAuth();

    const breadcrumbItems: PageBreadcrumbItem[] = [
        {
            label: t('Dashboard'),
            url: '/dashboard/home'
        },
        {
            label: t('My Profile'),
            active: true
        }
    ];

    // Form validation
    const [profileErrors] = useState<{
        email?: string, first_name?: string; last_name?: string;
    }>({});

    const [validated, setValidated] = useState(false);

    // State to control Toast messages
    const [show, setShow] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    const [profileImage, setProfileImage] = useState<any>(null);
    const [profile, setProfile] = useState<ProfileData>({
        profile_id: 0,
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        picture: null,  // base64
        location: '',

        company_department: '',
        linkedin_url: '',

        is_coach: 'off',
        is_mentor: 'off',

        about_me: '',
        inserted_at: '',
        contacts_count: 0,
        coach_types: '',
        mentor_types: '',

        question1: '', question2: '', question3: '',
        preferences: {
            careerCoaching: false,
            internalMobility: false,
            leadershipCoaching: false,
            mentoringLeadership: false,
            mentoringNewPosition: false,
            mentoringSales: false,
            shadowMentoring: false,
            teamCoaching: false,
        },
        interests: {
            art: false,
            cinemaTheatre: false,
            creativeWriting: false,
            cooking: false,
            reading: false,
            sport: false,
            technology: false,
            traveling: false,
            yogaMeditation: false,
        },
        services: '',
        coaching_method: ''
    });

    const fetchData = async () => {
        setLoading(true);
        try {
            const { data } = await apiClient.get('/v1/profile');
            setProfile(data);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        setFooterClass('d-none d-lg-block');
        return () => {
            setFooterClass('');
        };

    }, []);


    const handlePreferencesChange = (preferences: PreferencesState) => {
        setProfile({
            ...profile,
            preferences: preferences,
        });
    };

    const handleInterestsChange = (interests: InterestsState) => {
        setProfile({
            ...profile,
            interests: interests,
        });
    };

    const handleProfileChange = (name: string, value: any) => {
        setProfile({
            ...profile, [name]: value
        });
    };

    const handleProfileImageChange = async (image: File | null) => {
        if (image) {
            setProfileImage(image);
            const form = new FormData();
            form.append('file', image);

            try {
                const { data } = await apiClient.put(
                    `/v1/profile/${profile.profile_id}/image`, form,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',

                        }
                    }
                );

                setToastMessage(data['message'])
                if (auth.user) {
                    // Update the user state to trigger re-render of components that depend on user data
                    auth.setUser({
                        ...auth.user,
                        picture: data['picture']
                    });
                }

                setShow(true);
            } catch (error) {
                console.error('Request failed:', error);
            }

        }
    };



    const handleProfileAboutMeChange = async (aboutMe: string) => {
        const form = new FormData();
        form.append('about_me', aboutMe);

        try {
            const response = await apiClient.put(
                `/v1/profile/${profile.profile_id}/aboutme`, form,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',

                    }
                }
            );
            handleProfileChange("about_me", aboutMe);
            setToastMessage(response.data['message'])
            setShow(true);

        } catch (error) {
            console.error('Request failed:', error);
        }

    };

    const handleSubmit = async (event: any) => {
        event.preventDefault();
        event.stopPropagation();

        const form = event.currentTarget;
        if (form.checkValidity() === false) {
            console.log("invalid form");
            return
        }

        setValidated(true);

        const formData = new FormData();
        formData.append('profileForm', JSON.stringify(profile));
        try {
            const response = await apiClient.put(
                `/v1/profile`, formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',

                    }
                }
            );
            setToastMessage(response.data['message'])
            setShow(true);
        } catch (error) {
            console.error('Request failed:', error);
        }

    };


    if (loading) {
        return <div>Loading...</div>;
    }


    return (
        <>
            <PageBreadcrumb items={breadcrumbItems} />
            <div>
                <ToastContainer className="p-3" position='top-center'>
                    <Toast onClose={() => { setShow(false); }} show={show} delay={5000} autohide>
                        <Toast.Body>{toastMessage}</Toast.Body>
                    </Toast>
                </ToastContainer>
            </div>



            <Row className="g-6">
                <Col xs={12} xl={7}>
                    <ProfileCard avatar={profileImage ? profileImage : avatar} className="mb-3" showAbout={true} profile={profile} onProfileChange={handleProfileChange}
                        onChangeProfileImage={handleProfileImageChange}
                        onChangeAboutMe={handleProfileAboutMeChange}
                    />
                </Col>
                <Col xs={12} xl={5}>
                    <ProfileNavigation className="mb-3" />
                </Col>
            </Row>

            <PersonalInfo className="mb-3" profile={profile} onProfileChange={handleProfileChange} errors={profileErrors} />

            <CompanyInfo profile={profile} onProfileChange={handleProfileChange} className="mb-3" />

            <Card className='border border-light mb-4'>
                <Card.Body>
                    <Form noValidate validated={validated} onSubmit={handleSubmit}>
                        <div className="mb-1">


                            <Row className="g-6">
                                <SideBar profile={profile} onProfileChange={handleProfileChange} />
                            </Row>
                            <Row>
                                <Col xs={12}>
                                    {<Preferences preferences={profile.preferences} interests={profile.interests} onPreferencesChange={handlePreferencesChange}
                                        onInterestsChange={handleInterestsChange}
                                    />}
                                </Col>
                            </Row>

                            <Row>
                                <div className="text-end mb-6">
                                    <Button variant="phoenix-secondary" className="me-2" onClick={fetchData}>
                                        {t("UserProfile.cancel_changes")}
                                    </Button>
                                    <Button variant="phoenix-primary" type="submit">{t("UserProfile.save_information")}</Button>
                                </div>
                            </Row>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </>
    );
};

export default UserProfile;
